import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import { BIBlue, BILightGrey, BIRed } from "../../../assets/helpers";
import { useEffect } from "react";

const filterOptions = [
  {
    key: "location",
    label: "Location",
    description: "Search by Place",
    placeholder: "Location",
    icon: LocationOnOutlinedIcon
  },
  {
    key: "job",
    label: "Job",
    description: "Search by Job Details",
    placeholder: "Job code or name",
    icon: WorkIcon
  },
  {
    key: "employee",
    label: "Employee",
    description: "Search by Employee Details",
    placeholder: "Employee name",
    icon: PersonIcon
  }
];

const AssetFilterModal = ({
  filterModalOpen,
  setFilterModalOpen,
  draftFilters = {},
  setDraftFilters = () => {},
  jobOptions = [],
  jobLoading = false,
  employeeOptions = [],
  employeeLoading = false,
  handleClearAllFilters = () => {},
  handleFilterApply = () => {},
  selectedFilterOption,
  setSelectedFilterOption = () => {},
  accentColor,
  canViewAllAssets,
  empID,
  onJobSearch = () => {},
  onEmployeeSearch = () => {}
}) => {
  const shouldRestrictToSelf = !canViewAllAssets;
  useEffect(() => {
    if (shouldRestrictToSelf && empID) {
      setDraftFilters((prev) => ({
        ...prev,
        employeeId: empID
      }));
    }
  }, [shouldRestrictToSelf, empID, setDraftFilters]);

  const accent = accentColor ?? BIBlue;
  const selectedBg = accent ? `${accent}22` : "#ffe0e0";

  const handleFilterChange = (field) => (event) => {
    setDraftFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={() => setFilterModalOpen(false)}
      open={filterModalOpen}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Filter Assets
        <IconButton size="small" onClick={() => setFilterModalOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {filterOptions.map((option) => {
            const isSelected = selectedFilterOption === option.key;
            return (
              <Paper
                key={option.key}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: isSelected ? `1px solid ${accent}` : "1px solid rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  backgroundColor: isSelected ? selectedBg : "#fff"
                }}>
                <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row" onClick={() => setSelectedFilterOption(option.key)}
                  sx={{ cursor: "pointer" }}>
                  <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: BILightGrey,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                      <option.icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1">{option.label}</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {option.description}
                      </Typography>
                    </Box>
                  </Stack>
                  {isSelected ? (
                    <Chip label="Selected" size="small" sx={{ bgcolor: accent, color: "#fff" }} />
                  ) : (
                    <Box sx={{ width: 20 }} />
                  )}
                </Stack>
                {isSelected && (
                  <>
                    {option.key === "location" && (
                      <TextField
                        fullWidth
                        label={option.label}
                        placeholder={option.placeholder}
                        value={draftFilters.location}
                        onChange={handleFilterChange("location")}
                        sx={{ mt: 1 }}
                      />
                    )}
                    {option.key === "job" && (
                      <Autocomplete
                        freeSolo
                        options={jobOptions}
                        inputValue={draftFilters.job}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : (option?.label ?? "")
                        }
                        onInputChange={(_, value) => {
                          setDraftFilters((prev) => ({ ...prev, job: value, jobId: "" }));
                          onJobSearch(value);
                        }}
                        onChange={(_, option) => {
                          const label = typeof option === "string" ? option : (option?.label ?? "");
                          const id = typeof option === "object" ? (option?.id ?? "") : "";
                          setDraftFilters((prev) => ({ ...prev, job: label, jobId: id }));
                        }}
                        loading={jobLoading}
                        value={null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={option.label}
                            placeholder={option.placeholder}
                            sx={{ mt: 1 }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {jobLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps?.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    )}
                    {option.key === "employee" && (
                      <Autocomplete
                        freeSolo
                        options={employeeOptions}
                        inputValue={draftFilters.employee}
                        disabled={shouldRestrictToSelf}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : (option?.label ?? "")
                        }
                        onInputChange={(_, value) => {
                          setDraftFilters((prev) => ({ ...prev, employee: value, employeeId: "" }));
                          onEmployeeSearch(value);
                        }}
                        onChange={(_, option) => {
                          const label = typeof option === "string" ? option : (option?.label ?? "");
                          const id = typeof option === "object" ? (option?.id ?? "") : "";
                          setDraftFilters((prev) => ({
                            ...prev,
                            employee: label,
                            employeeId: id
                          }));
                        }}
                        loading={employeeLoading}
                        value={
                          shouldRestrictToSelf
                            ? employeeOptions.find((emp) => emp.id === empID) || null
                            : null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={option.label}
                            placeholder={option.placeholder}
                            sx={{ mt: 1 }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {employeeLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps?.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    )}
                  </>
                )}
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setFilterModalOpen(false)}
          sx={{ color: accent, textTransform: "none" }}>
          Cancel
        </Button>
        <Button onClick={handleClearAllFilters} sx={{ color: accent, textTransform: "none" }}>
          Clear All
        </Button>
        <Button
          variant="contained"
          onClick={handleFilterApply}
          sx={{
            backgroundColor: accent,
            color: "#fff",
            "&:hover": {
              backgroundColor: accent
            },
            textTransform: "none"
          }}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetFilterModal;
